import { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Canvas, Rect } from 'fabric';
import SignatureModal from './SignatureModal';
import ShareLinkModal from './ShareLinkModal';
import './DocumentEditor.css';

import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

const OWNER_FILL   = 'rgba(230,81,0,0.18)';
const OWNER_STROKE = '#e65100';
const RECIP_FILL   = 'rgba(21,101,192,0.14)';
const RECIP_STROKE = '#1565c0';

function makeFieldRect(type, left, top, w, h) {
  const r = new Rect({
    left, top, width: w, height: h,
    originX: 'left', originY: 'top',
    fill:               type === 'owner' ? OWNER_FILL   : RECIP_FILL,
    stroke:             type === 'owner' ? OWNER_STROKE : RECIP_STROKE,
    strokeWidth: 2, strokeDashArray: [6, 3],
    selectable: true, evented: true,
    hasControls: true, hasBorders: true,
    lockRotation: true,
    cornerSize: 10, transparentCorners: false,
    cornerColor: type === 'owner' ? OWNER_STROKE : RECIP_STROKE,
    borderColor: type === 'owner' ? OWNER_STROKE : RECIP_STROKE,
  });
  r.fieldType = type;
  return r;
}

function parseDocFields(doc) {
  if (doc.fields_json) {
    try {
      const fields = JSON.parse(doc.fields_json);
      // Only accept new relative-coordinate format (rx defined)
      return fields.filter(f => f.rx != null);
    } catch {}
  }
  return [];
}

export default function DocumentEditor({ doc, onBack }) {
  const pdfCanvasRef    = useRef(null);
  const fabricCanvasRef = useRef(null);
  const wrapRef         = useRef(null);
  const fabricRef       = useRef(null);
  const pdfDocRef       = useRef(null);
  const scaleRef        = useRef(1);
  const currentHRef     = useRef(0);
  const currentWRef     = useRef(0);
  const currentPageRef  = useRef(1);
  const renderTaskRef   = useRef(null);
  const drawnFieldsRef  = useRef(parseDocFields(doc)); // [{type, page, x, y, w, h}] PDF coords

  const [currentPage, setCurrentPage]         = useState(1);
  const [totalPages, setTotalPages]           = useState(1);
  const [fieldsSaved, setFieldsSaved]         = useState(drawnFieldsRef.current.length > 0);
  const [selectedType, setSelectedType]       = useState(null);
  const [ownerSigned, setOwnerSigned]         = useState(!!doc.owner_signed);
  const [drawMode, setDrawMode]               = useState(false);
  const [fieldVersion, setFieldVersion]       = useState(0); // force re-render when ref changes
  const [showSigModal, setShowSigModal]       = useState(false);
  const [showShare, setShowShare]             = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [signing, setSigning]                 = useState(false);
  const [savedSignature, setSavedSignature]   = useState(null);

  const bumpVersion = () => setFieldVersion(v => v + 1);

  // Snapshot current fabric rects into drawnFieldsRef (call before page change or save)
  const snapshotPage = useCallback((pageIndex) => {
    if (!fabricRef.current) return;
    const w    = currentWRef.current;
    const h    = currentHRef.current;
    if (!w || !h) return;
    const objs = fabricRef.current.getObjects().filter(o => o.fieldType);
    const snapped = objs.map(o => ({
      type: o.fieldType,
      page: pageIndex,
      rx: o.left / w,
      ry: o.top / h,
      rw: o.getScaledWidth() / w,
      rh: o.getScaledHeight() / h,
    }));
    drawnFieldsRef.current = [
      ...drawnFieldsRef.current.filter(f => f.page !== pageIndex),
      ...snapped,
    ];
  }, []);

  const calcScale = useCallback(async (pdf, pageNum) => {
    const page       = await pdf.getPage(pageNum);
    const nativeView = page.getViewport({ scale: 1 });
    const containerW = wrapRef.current ? wrapRef.current.clientWidth - 32 : window.innerWidth - 32;
    const maxW       = Math.min(containerW, 960);
    scaleRef.current = Math.max(0.5, maxW / nativeView.width);
    return page;
  }, []);

  const initFabric = useCallback((w, h, pageIndex) => {
    if (fabricRef.current) { fabricRef.current.dispose(); fabricRef.current = null; }
    currentHRef.current = h;
    currentWRef.current = w;
    const fc = new Canvas(fabricCanvasRef.current, { width: w, height: h, selection: false });
    fabricRef.current = fc;

    // Fabric wraps our canvas in a div.canvas-container with position:relative, which
    // doubles the layout height. Override it to absolute so it overlays the PDF canvas.
    const wrapper = fc.lowerCanvasEl?.parentNode ?? fabricCanvasRef.current?.parentElement;
    if (wrapper) {
      wrapper.style.position = 'absolute';
      wrapper.style.top      = '0';
      wrapper.style.left     = '0';
      wrapper.style.width    = `${w}px`;
      wrapper.style.height   = `${h}px`;
    }

    // Restore saved fields for this page
    const pageFields = drawnFieldsRef.current.filter(f => f.page === pageIndex);
    pageFields.forEach(field => {
      const r = makeFieldRect(field.type, field.rx * w, field.ry * h, field.rw * w, field.rh * h);
      fc.add(r);
    });
    if (pageFields.length) fc.renderAll();

    if (ownerSigned) return;

    // Selection tracking
    fc.on('selection:created',  (e) => setSelectedType(e.selected?.[0]?.fieldType ?? null));
    fc.on('selection:updated',  (e) => setSelectedType(e.selected?.[0]?.fieldType ?? null));
    fc.on('selection:cleared',  ()  => setSelectedType(null));
    // Mark unsaved when user moves/resizes
    fc.on('object:modified', () => setFieldsSaved(false));

    // Draw new rect on empty-space drag (only when draw-active)
    let isDown = false, origX = 0, origY = 0, drawingRect = null;
    fc.on('mouse:down', (o) => {
      if (o.target) return;
      isDown = true;
      const p = fc.getScenePoint(o.e);
      origX = p.x; origY = p.y; drawingRect = null;
    });
    fc.on('mouse:move', (o) => {
      if (!isDown) return;
      const p   = fc.getScenePoint(o.e);
      const l   = Math.min(p.x, origX);
      const t   = Math.min(p.y, origY);
      const ww  = Math.abs(p.x - origX);
      const hh  = Math.abs(p.y - origY);
      if (!drawingRect) {
        const type = computeDrawType();
        if (!type) { isDown = false; return; }
        drawingRect = makeFieldRect(type, l, t, ww, hh);
        drawingRect.set({ selectable: false, evented: false, hasControls: false });
        fc.add(drawingRect);
      } else {
        drawingRect.set({ left: l, top: t, width: ww, height: hh });
        drawingRect.setCoords();
      }
      fc.renderAll();
    });
    fc.on('mouse:up', () => {
      isDown = false;
      if (!drawingRect) return;
      const r = drawingRect;
      drawingRect = null;
      if (r.width < 20 || r.height < 10) { fc.remove(r); fc.renderAll(); return; }
      // Re-enable interaction after drawing
      r.set({ selectable: true, evented: true, hasControls: true });
      r.setCoords();
      fc.setActiveObject(r);
      fc.renderAll();
      snapshotPage(pageIndex);
      setFieldsSaved(false);
      bumpVersion();
      setDrawMode(false);
    });
  }, [ownerSigned, snapshotPage]);

  function computeDrawType() {
    const fields = drawnFieldsRef.current;
    if (!fields.some(f => f.type === 'owner'))     return 'owner';
    if (!fields.some(f => f.type === 'recipient')) return 'recipient';
    return null;
  }

  const renderPage = useCallback(async (pdf, pageNum) => {
    if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
    const page     = await calcScale(pdf, pageNum);
    const viewport = page.getViewport({ scale: scaleRef.current });
    const canvas   = pdfCanvasRef.current;
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const task = page.render({ canvasContext: ctx, viewport });
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch (e) {
      if (e?.name === 'RenderingCancelledException') return;
      throw e;
    }
    renderTaskRef.current = null;
    initFabric(canvas.width, canvas.height, pageNum - 1);
  }, [calcScale, initFabric]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      const [pdfRes, presetRes] = await Promise.all([
        fetch(`/api/documents/${doc.id}/pdf`, { credentials: 'include' }),
        fetch('/api/user/signature-preset', { credentials: 'include' }),
      ]);
      if (!pdfRes.ok) return;
      const buf = await pdfRes.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      pdfDocRef.current = pdf;
      setTotalPages(pdf.numPages);
      await renderPage(pdf, 1);
      if (presetRes.ok) {
        const { signaturePreset } = await presetRes.json();
        if (signaturePreset) setSavedSignature(signaturePreset);
      }
    };
    load();
    return () => {
      if (fabricRef.current) { fabricRef.current.dispose(); fabricRef.current = null; }
      if (renderTaskRef.current) renderTaskRef.current.cancel();
    };
  }, [doc.id]);

  // Page change
  useEffect(() => {
    if (!pdfDocRef.current) return;
    snapshotPage(currentPageRef.current - 1);
    currentPageRef.current = currentPage;
    renderPage(pdfDocRef.current, currentPage);
  }, [currentPage]);

  // Container resize
  useEffect(() => {
    if (!wrapRef.current) return;
    const observer = new ResizeObserver(() => {
      if (!pdfDocRef.current) return;
      snapshotPage(currentPageRef.current - 1);
      renderPage(pdfDocRef.current, currentPageRef.current);
    });
    observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, [renderPage]);

  const duplicateSelected = () => {
    const fc  = fabricRef.current;
    const sel = fc?.getActiveObject();
    if (!sel?.fieldType) return;
    const oppType = sel.fieldType === 'owner' ? 'recipient' : 'owner';
    // Don't duplicate if opposite type already exists
    if (drawnFieldsRef.current.some(f => f.type === oppType)) return;
    const r = makeFieldRect(oppType, sel.left + 24, sel.top + 24, sel.getScaledWidth(), sel.getScaledHeight());
    fc.add(r);
    fc.setActiveObject(r);
    fc.renderAll();
    snapshotPage(currentPageRef.current - 1);
    setFieldsSaved(false);
    bumpVersion();
  };

  const saveFields = async () => {
    snapshotPage(currentPageRef.current - 1);
    const fields = drawnFieldsRef.current;
    if (fields.length === 0) return;
    setSaving(true);
    await fetch(`/api/documents/${doc.id}/place-field`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    });
    setFieldsSaved(true);
    setSaving(false);
    bumpVersion();
  };

  const savePreset = async (signatureBase64) => {
    await fetch('/api/user/signature-preset', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signatureBase64 }),
    });
    setSavedSignature(signatureBase64);
  };

  const deletePreset = async () => {
    await fetch('/api/user/signature-preset', { method: 'DELETE', credentials: 'include' });
    setSavedSignature(null);
  };

  const handleOwnerSign = async (signatureBase64, doSavePreset) => {
    setShowSigModal(false); setSigning(true);
    if (doSavePreset) savePreset(signatureBase64);
    const res = await fetch(`/api/documents/${doc.id}/sign/owner`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signatureBase64 }),
    });
    if (res.ok) {
      setOwnerSigned(true); doc.owner_signed = 1; doc.status = 'pending';
      const buf = await (await fetch(`/api/documents/${doc.id}/pdf`, { credentials: 'include' })).arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      pdfDocRef.current = pdf;
      await renderPage(pdf, currentPage);
    }
    setSigning(false);
  };

  const fields       = drawnFieldsRef.current; // only for render — fieldVersion drives re-reads
  const hasOwner     = fields.some(f => f.type === 'owner');
  const hasRecipient = fields.some(f => f.type === 'recipient');
  const drawType     = computeDrawType();
  const canDuplicate = selectedType && (selectedType === 'owner' ? !hasRecipient : !hasOwner);
  const canSave      = !fieldsSaved && fields.length > 0 && !ownerSigned;
  const canSign      = fieldsSaved && hasOwner && !ownerSigned && !signing;
  const canShare     = ownerSigned;
  void fieldVersion; // consumed to trigger re-renders

  return (
    <div className="doc-editor">
      <div className="doc-editor-toolbar">
        <button className="editor-btn secondary" onClick={onBack}>← Terug</button>
        <h4 title={doc.filename}>{doc.filename}</h4>

        {!ownerSigned && drawType && !drawMode && (
          <button className="editor-btn draw-toggle" onClick={() => setDrawMode(true)}>
            + {drawType === 'owner' ? 'Mijn veld' : 'Veld ontvanger'}
          </button>
        )}
        {!ownerSigned && drawMode && (
          <button className="editor-btn secondary" onClick={() => setDrawMode(false)}>Tekenen annuleren</button>
        )}
        {!ownerSigned && canDuplicate && !drawMode && (
          <button className="editor-btn draw-toggle" style={{ borderColor: selectedType === 'owner' ? RECIP_STROKE : OWNER_STROKE, color: selectedType === 'owner' ? RECIP_STROKE : OWNER_STROKE }}
            onClick={duplicateSelected}>
            Dupliceren als {selectedType === 'owner' ? 'ontvanger' : 'mijn veld'}
          </button>
        )}

        <span className="field-legend">
          {hasOwner     && <span className="legend-dot owner" />}
          {hasOwner     && <span className="legend-label">Mijn veld</span>}
          {hasRecipient && <span className="legend-dot recipient" />}
          {hasRecipient && <span className="legend-label">Ontvanger</span>}
        </span>

        {canSave  && <button className="editor-btn primary" onClick={saveFields} disabled={saving}>{saving ? 'Bezig met opslaan…' : 'Velden Opslaan'}</button>}
        {canSign  && <button className="editor-btn success" onClick={() => setShowSigModal(true)}>Ondertekenen</button>}
        {signing  && <span style={{ fontSize: '0.85rem', color: '#888' }}>Bezig met ondertekenen…</span>}
        {canShare && <button className="editor-btn share" onClick={() => setShowShare(true)}>Delen</button>}
        {ownerSigned && <span className="field-status signed">✓ Ondertekend</span>}
      </div>

      {drawMode && (
        <div className="draw-mode-banner">
          Sleep over de PDF om het vak voor <strong>{drawType === 'owner' ? 'mijn handtekening' : 'handtekening ontvanger'}</strong> te plaatsen
        </div>
      )}

      <div className="doc-editor-canvas-wrap" ref={wrapRef}>
        <div className={`canvas-stack${drawMode ? ' draw-active' : ''}${fields.length > 0 || ownerSigned ? ' has-fields' : ''}`}>
          <canvas ref={pdfCanvasRef} />
          <canvas ref={fabricCanvasRef} style={{ position: 'absolute', top: 0, left: 0 }} />
        </div>
      </div>

      {totalPages > 1 && (
        <div className="page-nav">
          <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>‹ Vorige</button>
          <span>Pagina {currentPage} van {totalPages}</span>
          <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Volgende ›</button>
        </div>
      )}

      {showSigModal && <SignatureModal onConfirm={handleOwnerSign} onClose={() => setShowSigModal(false)} savedSignature={savedSignature} onDeletePreset={deletePreset} />}
      {showShare    && <ShareLinkModal docId={doc.id} onClose={() => setShowShare(false)} />}
    </div>
  );
}
